import React, { useEffect, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import {
  Button,
  ButtonType,
  Drawer,
  Input,
  Select,
  DatePicker,
  InputNumber
} from 'syngenta-digital-cropwise-react-ui-kit';

import Close from 'assets/close.svg';
import ChevronDown from 'assets/chevron_down.svg?react';
import ChevronUp from 'assets/chevron_up.svg?react';
import moment from 'moment';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import {
  COL_SPAN_FULL,
  COL_SPAN_HALF,
  COL_SPAN_ONE_FOURTH
} from 'components/FormGrid/constants';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import {
  AddNewEquipmentDrawerProps,
  EquipmentFormValues
} from 'components/EquipmentDropdown/types';
import useEquipmentData from 'hooks/useEquipmentData';
import { EQUIPMENT_SCHEMA } from './schemas';
import { STATUSES, TYPES, INITIAL_VALUES } from './constants';
import mapEquipmentFormValuesToApi from './helpers/mapEquipmentFormValuesToApi';
import './index.css';

const { TextArea } = Input;

const AddNewEquipmentDrawer: React.FC<AddNewEquipmentDrawerProps> = ({
  onClose,
  onEquipmentAdded,
  open,
  title = 'Create New Equipment'
}) => {
  const { createEquipment, saving } = useEquipmentData();
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const [openDropdowns, setOpenDropdowns] = React.useState<{
    type: boolean;
    currentStatus: boolean;
  }>({
    type: false,
    currentStatus: false
  });

  const showChevron = (key: 'type' | 'currentStatus'): React.ReactNode => {
    return openDropdowns[key] ? (
      <ChevronUp width={18} height={18} />
    ) : (
      <ChevronDown width={18} height={18} />
    );
  };

  const handleDropdownVisibleChange = (
    _open: boolean,
    field: 'type' | 'currentStatus'
  ): void => {
    setOpenDropdowns(prev => ({
      ...prev,
      [field]: _open
    }));
  };

  const handleSubmit = async (values: EquipmentFormValues) => {
    const mappedValues = mapEquipmentFormValuesToApi(values);
    const newEquipment = await createEquipment(mappedValues);
    if (newEquipment) {
      onEquipmentAdded(newEquipment);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    focusInput(drawerContentRef, 'input[name="name"]');
  }, [open]);

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={EQUIPMENT_SCHEMA}
      onSubmit={handleSubmit}
    >
      {(formikProps: FormikProps<EquipmentFormValues>) => {
        const {
          resetForm,
          submitForm,
          setFieldValue,
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          isValid,
          dirty
        } = formikProps;

        const handleCancel = () => {
          resetForm({ values: INITIAL_VALUES });
          onClose();
        };

        const formRows: FormRowConfig[] = [
          {
            id: 'required-fields-row',
            fields: [
              {
                id: 'name',
                label: 'Name',
                required: true,
                colSpan: COL_SPAN_HALF,
                error: touched.name ? errors.name : undefined,
                render: () => (
                  <Input
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                    status={errors.name && touched.name ? 'error' : undefined}
                  />
                )
              },
              {
                id: 'type',
                label: 'Type',
                required: true,
                colSpan: COL_SPAN_HALF,
                error: touched.type ? errors.type : undefined,
                render: () => (
                  <Select
                    id="type"
                    value={values.type}
                    onChange={value => setFieldValue('type', value)}
                    onBlur={() => handleBlur({ target: { name: 'type' } })}
                    style={{ width: '100%' }}
                    status={errors.type && touched.type ? 'error' : undefined}
                    showSearch
                    filterOption={filterOptionByNormalizedString}
                    className="custom-select"
                    suffixIcon={showChevron('type')}
                    onDropdownVisibleChange={(_open: boolean) =>
                      handleDropdownVisibleChange(_open, 'type')
                    }
                  >
                    {TYPES.map(type => (
                      <Select.Option key={type.value} value={type.value}>
                        {type.label}
                      </Select.Option>
                    ))}
                  </Select>
                )
              }
            ]
          },
          // Current status row
          {
            id: 'current-status-row',
            fields: [
              {
                id: 'currentStatus',
                label: 'Current Status',
                required: true,
                colSpan: COL_SPAN_HALF,
                error: touched.currentStatus ? errors.currentStatus : undefined,
                render: () => (
                  <Select
                    id="currentStatus"
                    value={values.currentStatus}
                    onChange={value => setFieldValue('currentStatus', value)}
                    onBlur={() =>
                      handleBlur({ target: { name: 'currentStatus' } })
                    }
                    style={{ width: '100%' }}
                    status={
                      errors.currentStatus && touched.currentStatus
                        ? 'error'
                        : undefined
                    }
                    className="custom-select"
                    suffixIcon={showChevron('currentStatus')}
                    onDropdownVisibleChange={(_open: boolean) =>
                      handleDropdownVisibleChange(_open, 'currentStatus')
                    }
                  >
                    {STATUSES.map(status => (
                      <Select.Option key={status.value} value={status.value}>
                        {status.label}
                      </Select.Option>
                    ))}
                  </Select>
                )
              },
              {
                id: 'currentStatusDate',
                label: 'Current Status Date',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <DatePicker
                    name="currentStatusDate"
                    value={
                      values.currentStatusDate
                        ? moment(values.currentStatusDate)
                        : undefined
                    }
                    onChange={date => setFieldValue('currentStatusDate', date)}
                    onBlur={() =>
                      handleBlur({ target: { name: 'currentStatusDate' } })
                    }
                    style={{ width: '100%' }}
                    format="MM-DD-YYYY"
                    placeholder=""
                    getPopupContainer={getDropdownParentWithoutDetaching}
                  />
                )
              }
            ]
          },
          // Basic Info section header
          {
            id: 'basic-info-header',
            fields: [
              {
                id: 'basic-info-title',
                colSpan: COL_SPAN_FULL,
                isHeader: true,
                render: () => (
                  <div className="text-base font-bold mb-4">Basic Info</div>
                )
              }
            ]
          },
          // Year, Make, Model row
          {
            id: 'year-make-model-row',
            fields: [
              {
                id: 'year',
                label: 'Year',
                colSpan: COL_SPAN_ONE_FOURTH,
                error: touched.year ? errors.year : undefined,
                render: () => (
                  <Input
                    name="year"
                    value={values.year}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                    status={errors.year && touched.year ? 'error' : undefined}
                  />
                )
              },
              {
                id: 'make',
                label: 'Make',
                colSpan: COL_SPAN_ONE_FOURTH,
                render: () => (
                  <Input
                    name="make"
                    value={values.make}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                  />
                )
              },
              {
                id: 'model',
                label: 'Model',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <Input
                    name="model"
                    value={values.model}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                  />
                )
              }
            ]
          },
          // Unit ID and Serial Number row
          {
            id: 'unit-serial-row',
            fields: [
              {
                id: 'unitId',
                label: 'Unit ID',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <Input
                    name="unitId"
                    value={values.unitId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                  />
                )
              },
              {
                id: 'serialNumber',
                label: 'Serial Number',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <Input
                    name="serialNumber"
                    value={values.serialNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                  />
                )
              }
            ]
          },
          // License Number row
          {
            id: 'license-row',
            fields: [
              {
                id: 'licenseNumber',
                label: 'License Number',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <Input
                    name="licenseNumber"
                    value={values.licenseNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                  />
                )
              }
            ]
          },
          // Purchase History section header
          {
            id: 'purchase-history-header',
            fields: [
              {
                id: 'purchase-history-title',
                colSpan: COL_SPAN_FULL,
                isHeader: true,
                render: () => (
                  <div className="text-base font-bold mb-4">
                    Purchase History
                  </div>
                )
              }
            ]
          },
          // Purchase Price and Date row
          {
            id: 'purchase-row',
            fields: [
              {
                id: 'purchasePrice',
                label: 'Purchase Price',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <InputNumber
                    name="purchasePrice"
                    value={
                      values.purchasePrice ? Number(values.purchasePrice) : null
                    }
                    onChange={value => setFieldValue('purchasePrice', value)}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                    prefix="$"
                    min={0}
                    precision={2}
                    controls={false}
                  />
                )
              },
              {
                id: 'purchaseDate',
                label: 'Purchase Date',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <DatePicker
                    name="purchaseDate"
                    value={
                      values.purchaseDate
                        ? moment(values.purchaseDate)
                        : undefined
                    }
                    onChange={date => setFieldValue('purchaseDate', date)}
                    onBlur={() =>
                      handleBlur({ target: { name: 'purchaseDate' } })
                    }
                    style={{ width: '100%' }}
                    format="MM-DD-YYYY"
                    placeholder=""
                    getPopupContainer={getDropdownParentWithoutDetaching}
                  />
                )
              }
            ]
          },
          // Current Value section header
          {
            id: 'current-value-header',
            fields: [
              {
                id: 'current-value-title',
                colSpan: COL_SPAN_FULL,
                isHeader: true,
                render: () => (
                  <div className="text-base font-bold mb-4">Current Value</div>
                )
              }
            ]
          },
          // Current Value and Date row
          {
            id: 'current-value-row',
            fields: [
              {
                id: 'currentValue',
                label: 'Current Value',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <InputNumber
                    name="currentValue"
                    value={
                      values.currentValue ? Number(values.currentValue) : null
                    }
                    onChange={value => setFieldValue('currentValue', value)}
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                    prefix="$"
                    min={0}
                    precision={2}
                    controls={false}
                  />
                )
              },
              {
                id: 'currentValueDate',
                label: 'Current Value Date',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <DatePicker
                    name="currentValueDate"
                    value={
                      values.currentValueDate
                        ? moment(values.currentValueDate)
                        : undefined
                    }
                    onChange={date => setFieldValue('currentValueDate', date)}
                    onBlur={() =>
                      handleBlur({ target: { name: 'currentValueDate' } })
                    }
                    style={{ width: '100%' }}
                    format="MM-DD-YYYY"
                    placeholder=""
                    getPopupContainer={getDropdownParentWithoutDetaching}
                  />
                )
              }
            ]
          },
          // Notes row
          {
            id: 'notes-row',
            fields: [
              {
                id: 'notes',
                label: 'Notes',
                colSpan: COL_SPAN_FULL,
                render: () => (
                  <TextArea
                    name="notes"
                    value={values.notes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className="resize-none w-full"
                    aria-label="Equipment notes"
                    autoSize={false}
                    style={{ height: '80px' }}
                  />
                )
              }
            ]
          }
        ];

        return (
          <Drawer
            maskClosable
            open={open}
            className="cwf-add-equipment-drawer"
            placement="right"
            closable={false}
            onClose={handleCancel}
            bodyStyle={{ padding: 0 }}
            footerStyle={{ borderTop: '1px solid #dfe2e7' }}
            footer={
              <div className="flex justify-between">
                <Button
                  type={ButtonType.outline}
                  onClick={handleCancel}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  className="company-people-add-save-button"
                  type={ButtonType.primary}
                  onClick={submitForm}
                  loading={saving}
                  disabled={saving || !dirty || !isValid}
                  size="large"
                >
                  Save Equipment
                </Button>
              </div>
            }
            width="50%"
          >
            <div className="relative">
              <div className="absolute top-0 right-0 m-3">
                <button
                  type="button"
                  data-testid="close-form"
                  onClick={handleCancel}
                >
                  <img src={Close} alt="Close icon" width={20} height={20} />
                </button>
              </div>
            </div>
            <div className="equipment-form p-6" ref={drawerContentRef}>
              <div
                className="flex gap-4 items-center mb-6"
                data-testid="equipment-container"
              >
                <h1 className="text-xl font-semibold block mb-0 mt-2">
                  {title}
                </h1>
              </div>

              <FormGrid rows={formRows} />
            </div>
          </Drawer>
        );
      }}
    </Formik>
  );
};

export default AddNewEquipmentDrawer;
